import { useState } from "react";
import { ArrowOutward, Close, DeleteOutline } from "@mui/icons-material";
import { Box, Button, Dialog, DialogActions, DialogContent, Grid, IconButton, Typography } from "@mui/material";
import { toast } from "react-toastify";
import { translate } from "../../../../../translate/translate";
import { BaseService } from "../../../../../services/base-service";
import { URLS as API_URLS } from "../../../../../services/app-urls";
import { ErrorComponent } from "../../../../../components/error/error.component";
import { calculateProgressSegments, getProjectProgressMetrics } from "../utils/project.utils";
import { projectHeaderSx } from "../constants/dashboard.constants";
import { NpiProject } from "../types/npi-project.types";
import { URLS } from "../../../../../services/routes";
import { useNavigate } from "react-router-dom";

export const NpiDashboardProjectHeaderComponent = ({ project, onDeleted }: { project: NpiProject; onDeleted: () => void }) => {
  const navigate = useNavigate();
  const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean>(false);

  const progressSegments = calculateProgressSegments(project);
  const progress = getProjectProgressMetrics(project).progress;

  const handleOpenProject =(id: number) => {
    navigate(URLS.NPI_PROJECT_PLANNING.replace(":id", String(id)));
  };

  const handleConfirmDelete = () => {
    new BaseService<NpiProject>(API_URLS.NPI).delete(project.id!!).then(() => {
      toast.success(translate.t("successfully_deleted"));
      setOpenDeleteDialog(false);
      onDeleted();
    }).catch((error: any) => {
      ErrorComponent(error);
    });
  };

  return (
    <Box onClick={() => handleOpenProject(project.id!!)} sx={projectHeaderSx}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          width: "auto",
        }}
      >
        <Typography
          noWrap
          sx={{ fontSize: "15px", fontWeight: 700, color: "#1E293B", flex: 1, minWidth: 0 }}
        >
          {project.projectName}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", flexShrink: 0, width: "auto" }}>
          <ArrowOutward sx={{ fontSize: 16, color: "#64748B" }} />
          <IconButton
            size="small"
            data-export-ignore
            aria-label={translate.t("delete")}
            sx={{ p: 0 }}
            onClick={(event) => {
              event.stopPropagation();
              setOpenDeleteDialog(true);
            }}>
            <DeleteOutline sx={{ fontSize: 16, color: "#64748B" }} />
          </IconButton>
        </Box>
      </Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          mt: 0.75,
          width: "auto",
        }}
      >
        <Box
          sx={{
            flex: 1,
            width: "auto",
            height: 8,
            borderRadius: 4,
            backgroundColor: "#E2E8F0",
            display: "flex",
            overflow: "hidden",
          }}
        >
          {progressSegments.map((segment) => (
            <Box
              key={segment.color}
              sx={{
                width: `${segment.width}%`,
                backgroundColor: segment.color,
              }}
            />
          ))}
        </Box>
        <Typography
          sx={{
            fontSize: "12px",
            color: progress > 0 ? "#1E293B" : "#94A3B8",
          }}
        >
          {progress}%
        </Typography>
      </Box>
      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)} onClick={(event) => event.stopPropagation()}>
        <Grid p={2} container justifyContent="space-between" sx={{ backgroundColor: "primary.main" }}>
          <Grid>
            <Typography sx={{ color: "#ffffff", fontWeight: "bold", fontSize: "16px" }}>
              {translate.t("confirm_delete_header")}
            </Typography>
          </Grid>
          <Grid sx={{ cursor: "pointer", color: "#ffffff" }}>
            <Close onClick={() => setOpenDeleteDialog(false)} />
          </Grid>
        </Grid>
        <DialogContent>
          <Grid container flexDirection="column" gap={2}>
            <Typography>{translate.t("confirm_delete_npi")}</Typography>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Grid container justifyContent="flex-end" gap={1} p={2}>
            <Button color="secondary" variant="outlined" onClick={() => setOpenDeleteDialog(false)}>
              {translate.t("cancel")}
            </Button>
            <Button variant="contained" color="error" onClick={handleConfirmDelete}>
              {translate.t("delete")}
            </Button>
          </Grid>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
